import { sagaCreator, stateSelector } from 'redux-saga-kit';
import { all, put, call, select, take } from 'redux-saga/effects';
import * as actions from '../engine/actions/SessionActions';
import StepHandlers from './StepHandlers';
import { MENUS_APP_DEF_ID, Modals } from '../../utils/constants';
import BILogger from '../../utils/BILogger';
import { PageComponentId } from '@wix/business-manager-api';
import { eventChannel } from 'redux-saga';
import { FLOW_STEP, LOCAL_STORAGE_FIRST_TIME_KEY } from '../engine/reducers/SessionReducer';
import { setPaymentMetadata, setPaymentMethods } from '../engine/actions/CashierActions';
import { isUsingCashier } from '../../utils/CashierUtils';
import moment from 'moment';
import { setMomentLocale } from '../../utils/i18nUtils';

export const ROLES_AND_PERMISSIONS_PATH = {
  pageComponentId: PageComponentId.SiteSettings,
  contextData: {
    appState: 'authorization',
  },
};

function createOnNavigationChannel(onNavigation) {
  return eventChannel(emit => {
    const { remove } = onNavigation(pause => emit(pause));
    return remove;
  });
}

const onInit = ({
  authentication,
  restClient,
  wixApiClient,
  mappingClient,
  env,
  reportSystemIsFullyLoaded,
  onNavigation,
}) =>
  function*() {
    const instance = env.instance;
    const fallbackInstance = env.fallbackInstance;

    yield all([
      call(getAccessToken({ authentication }), instance),
      call(getFullOrganization({ restClient, mappingClient }), { instance, fallbackInstance }),
    ]);

    const organization = yield select(stateSelector('session.organization'));
    if (isUsingCashier(organization)) {
      yield all([
        call(getCashierPaymentMethods({ wixApiClient }), { instance }),
        call(getCashierPaymentMetadata({ wixApiClient })),
      ]);
    }

    const organizationId = yield select(stateSelector('session.organization.id'));
    const locale = yield select(stateSelector('session.organization.locale'));

    yield call(setMomentLocale, { moment, locale });

    reportSystemIsFullyLoaded({ organizationId });

    const onNavigationChannel = createOnNavigationChannel(onNavigation);
    yield call(navigateAwayHandler, onNavigationChannel);
  };

function* navigateAwayHandler(onNavigationChannel) {
  const safeSessionSteps = [FLOW_STEP.INIT, FLOW_STEP.IDLE];

  while (true) {
    const pauseNavigation = yield take(onNavigationChannel);

    const step = yield select(stateSelector('session.step'));

    if (!safeSessionSteps.includes(step)) {
      const { resume } = pauseNavigation();
      yield put(actions.displayModal({ modal: Modals.NAVIGATE_AWAY }));
      const { payload } = yield take(actions.navigateAwayModalClose);
      if (payload.shouldResumeNavigation) {
        resume();
      }
    }
  }
}

////////////// effects

const getAccessToken = ({ authentication }) =>
  function*(instance) {
    let accessToken;

    try {
      const authenticationResponse = yield call(authentication, '/com.wix/access_token', 'post', { instance });

      accessToken = authenticationResponse.accessToken;
    } catch (e) {
      yield put(actions.accessTokenError({ error: e }));
    }

    if (accessToken) {
      yield put(actions.accessTokenSuccess({ accessToken }));
    }
  };

function extractInstanceIdFromInstance(instance) {
  try {
    return JSON.parse(atob(instance.split('.')[1])).instanceId;
  } catch (e) {
    return null;
  }
}

function* getOrganizationIdFromMapping({ mappingClient, instance, fallbackInstance }) {
  const instanceId = extractInstanceIdFromInstance(instance);
  const fallbackInstanceId = extractInstanceIdFromInstance(fallbackInstance);

  try {
    const mapping = yield call(mappingClient, `/instances/${instanceId}`);
    return mapping.organizationId;
  } catch (e) {
    const mapping = yield call(mappingClient, `/instances/${fallbackInstanceId}`);
    return mapping.organizationId;
  }
}

const getFullOrganization = ({ restClient, mappingClient }) =>
  function*({ restaurantId, instance, fallbackInstance }) {
    try {
      yield put(actions.indicateFetchOrganization());
      let organizationId = restaurantId;

      if (!organizationId) {
        organizationId = yield call(getOrganizationIdFromMapping, { mappingClient, instance, fallbackInstance });
      }
      const full = yield call(restClient, `/organizations/${organizationId}/full`, 'get');
      yield put(actions.fullOrganizationSuccess({ full }));
    } catch (e) {
      yield put(actions.fullOrganizationError({ error: e }));
    }
  };

function getCashierPaymentMethods({ wixApiClient }) {
  return function*({ instance }) {
    const instanceId = extractInstanceIdFromInstance(instance);
    const accountId = `${MENUS_APP_DEF_ID}:${instanceId}`;
    const url = `/_api/payment-services-web/payments/v2/accounts/${accountId}/payment-methods`;
    const { paymentMethods } = yield call(wixApiClient, url);
    yield put(setPaymentMethods({ paymentMethods }));
  };
}

function getCashierPaymentMetadata({ wixApiClient }) {
  return function*() {
    const organization = yield select(stateSelector('session.organization'));
    const language = organization.locale.substr(0, 2);
    const country = organization.address.countryCode.toUpperCase();
    const url = `/_api/payment-metadata-web/plugins/data?country=${country}&locale=${language}`;
    const paymentMetadata = yield call(wixApiClient, url);
    yield put(setPaymentMetadata({ paymentMetadata }));
  };
}

function onFetchBranches({ restClient }) {
  return function*({ payload }) {
    try {
      const { chainId } = payload;
      const { restaurantIds } = yield call(restClient, `/organizations/${chainId}/branches`, 'get');
      const concatenatedIds = restaurantIds.join(',');
      const { organizations } = yield call(
        restClient,
        `/organizations?id=${concatenatedIds}&fields=id,locale,title,deliveryInfos,address,distributorId,orders`,
        'get',
      );
      yield put(actions.fetchBranchesSuccess({ organizations }));
    } catch (e) {
      yield put(actions.fetchBranchesFailure());
    }
  };
}

function onSelectBranch({ restClient }) {
  return function*({ payload }) {
    const { branchId } = payload;
    yield all([
      take(actions.fullOrganizationSuccess),
      call(getFullOrganization({ restClient }), { restaurantId: branchId }),
    ]);
    yield put(actions.hideModal({ modal: Modals.BRANCH_SELECTOR }));
  };
}

function onDisplayModal() {
  return function*({ payload }) {
    const { modal } = payload;
    if (modal === Modals.BRANCH_SELECTOR) {
      const chainId = yield select(stateSelector('session.organization.chainId'));
      yield put(actions.fetchBranches({ chainId }));
    }
  };
}

function onHideModal() {
  return function*({ payload }) {
    const { modal } = payload;
    if (modal === Modals.BRANCH_SELECTOR) {
      yield put(actions.clearBranches());
    }
  };
}

function* onReportCustomerInformationContinueAttempt({ payload }) {
  const organizationId = yield select(stateSelector('session.organization.id'));
  const { errorField, isError, isNewCustomer } = payload;

  BILogger.customerInformationClickContinue({
    errorField,
    isError,
    isNewCustomer,
    organizationId,
  });
}

function navigateToSettings({ navigateTo }) {
  return function navigateToSettingsImpl() {
    navigateTo({
      pageComponentId: 'restaurants',
      contextData: { appState: 'ordersSettings' },
    });
  };
}

function navigateToRolesAndPermissions({ navigateTo }) {
  return function navigateToSettingsImpl() {
    navigateTo(ROLES_AND_PERMISSIONS_PATH);
  };
}

function firsTimeHandler({ localStorage }) {
  return function*({ payload }) {
    if (payload.value === false) {
      localStorage.setItem(LOCAL_STORAGE_FIRST_TIME_KEY, LOCAL_STORAGE_FIRST_TIME_KEY);
    }
  };
}
////////////// default export

export default sagaCreator(
  Object.assign(
    {
      [actions.init]: { handler: onInit },
      [actions.fetchBranches]: { handler: onFetchBranches },
      [actions.selectBranch]: { handler: onSelectBranch },
      [actions.displayModal]: { handler: onDisplayModal },
      [actions.hideModal]: { handler: onHideModal },
      [actions.reportCustomerInformationContinueAttempt]: {
        handler: onReportCustomerInformationContinueAttempt,
        noParams: true,
      },
      [actions.goToSettings]: { handler: navigateToSettings },
      [actions.setFirstTime]: { handler: firsTimeHandler },
      [actions.goToRolesAndPermissions]: { handler: navigateToRolesAndPermissions },
    },
    StepHandlers,
  ),
);
